<div
    [ngClass]="{
        carousel: true,
        'server-centered-slides': isServerMode && centerInsufficientSlides,
        'single-slide-per-view': isSingleSlide(),
        'auto-slides': slidesPerView === 'auto',
        'sliding-next': slidingDirection() === 1,
        'sliding-prev': slidingDirection() === -1,
        'optimize-transform': optimizeTransformAnimation
    }"
    aria-roledescription="carousel"
    i18n-aria-roledescription
    [attr.aria-labelledby]="'carousel-title-' + carouselTitle"
    [style]="carouselWrapperCustomCSS"
    (mouseleave)="onMouseleave()"
    (mouseenter)="onFocusIn()"
    (keydown)="onFocusIn()"
    #carousel>
    <!-- Here you should include the title of your carousel, it is MANDATORY because it is used in the aria-labelledby for A11Y-->
    <div hSrOnlyNoSnippet id="carousel-title-{{ carouselTitle }}">
        <ng-content></ng-content>
    </div>

    <swiper
        id="carousel-items-{{ carouselTitle }}"
        #swiper
        [config]="carouselConfiguration"
        [class.flex-center-slide]="flexCenterSlide"
        (slideChangeTransitionEnd)="onSlideChangeTransitionEnd()"
        (transitionEnd)="transitionEnd.emit()"
        (slideChange)="onSlideChange()"
        (breakpoint)="onBreakpointChange($event[1])"
        (scroll)="sliderFirstMove.emit()"
        (sliderFirstMove)="sliderFirstMove.emit()"
        (slideNextTransitionStart)="slidingTo(1)"
        (slidePrevTransitionStart)="slidingTo(-1)"
        (reachEnd)="onReachEnd()"
        (reachBeginning)="onReachBeginning()"
        (afterInit)="(assets ?? []).length === 0 ? onImageReady() : ''">
        <!-- Slides display -->
        @for (slide of slides; track slide) {
        <ng-template swiperSlide>
            <ng-container *ngTemplateOutlet="slide"></ng-container>
        </ng-template>
        } @if (assets) { @for (asset of assets; track trackByAsset(idx); let idx = $index) {

        <div
            class="asset-wrapper"
            id="carousel-item-{{ carouselTitle }}"
            aria-roledescription="slide"
            i18n-aria-roledescription
            [attr.aria-labelledby]="'carousel-item-label-' + idx + '-' + carouselTitle"
            (click)="clickOnAsset.emit({ carouselCurrentIndex: swiper?.swiperRef?.realIndex, assetType: asset.type })"
            *swiperSlide>
            @if (asset.type !== assetMediaType.Video) { @if (!wrapAssetInsideButton) {

            <!-- Use web lazy because the data-src of swiper lazy does not refresh image when change variant -->
            <img
                [alt]="imageAlt || asset.title"
                class="custom-focus"
                [tabindex]="idx === swiper?.swiperRef?.realIndex && assets.length > 1 ? '0' : '-1'"
                [src]="asset.url"
                (load)="onImageReady()"
                (keydown)="onFocusIn()"
                (focusout)="onFocusOut($event)"
                [attr.loading]="imageFetchPriority && idx === 0 ? 'eager' : 'lazy'"
                [attr.fetchpriority]="imageFetchPriority && idx === 0 ? 'high' : 'auto'" />

            } @else {

            <button
                aria-haspopup="dialog"
                i18n-aria-haspopup
                [attr.aria-labelledby]="'zoom-label-' + idx + '-' + carouselTitle"
                aria-expanded="false"
                class="custom-focus"
                [tabindex]="idx === swiper?.swiperRef?.realIndex || assets.length === 1 ? '0' : '-1'"
                (keydown)="onFocusIn()"
                (focusout)="onFocusOut($event)">
                <img
                    [alt]="imageAlt || asset.title"
                    [src]="asset.url"
                    (load)="onImageReady()"
                    [attr.loading]="imageFetchPriority && idx === 0 ? 'eager' : 'lazy'"
                    [attr.fetchpriority]="imageFetchPriority && idx === 0 ? 'high' : 'auto'" />
            </button>

            } } @else {

            <h-video
                [posterUrl]="asset.posterUrl"
                [videoName]="asset.title ?? ''"
                [alwaysShowControls]="true"
                [isAriaHidden]="idx !== swiper?.swiperRef?.realIndex || isMobile"
                [disableControlTabIndex]="idx !== swiper?.swiperRef?.realIndex"
                [isLightMode]="true"
                [isPlaying]="swiper?.swiperRef?.realIndex === 1 && isPlayVideo === true"
                [mediaType]="'snackable_video'"
                [videoUrl]="asset.url"
                [videoRatio]="'1-1'"
                [controlSize]="controlSize"
                [controlPosition]="videoControlPosition"
                (clickOnPlayPauseVideoControls)="onPlayPauseVideo($event)"
                [tabindex]="(idx === swiper?.swiperRef?.realIndex || assets.length === 1) && isMobile ? '0' : '-1'"
                (keydown)="onFocusIn()"
                (focusout)="onFocusOut($event)">
                <img alt="" [src]="asset.posterUrl" loading="lazy" />
            </h-video>

            }

            <div hSrOnlyNoSnippet id="carousel-item-label-{{ idx }}-{{ carouselTitle }}">
                @if (idx >= 0 && idx <= assets.length) {
                <div>
                    <span
                        hI18nAsset
                        [tag]="asset.tag"
                        [hI18nAttr]="'innerText'"
                        [imageTitle]="asset.tag"
                        [index]="idx"
                        [url]="asset.type === 'video' && asset.posterUrl ? asset.posterUrl : asset.url">
                    </span>
                    <ng-container i18n="@@hermes.accessibility.comma-separator">,&nbsp;</ng-container>
                    <ng-container i18n="@@hermes.accessibility.carousel.slide">
                        {{ idx + 1 }} of {{ assets.length }}
                    </ng-container>
                    @if (wrapAssetInsideButton) {
                    <span id="zoom-label-{{ idx }}-{{ carouselTitle }}">
                        <ng-container i18n="@@hermes.accessibility.comma-separator">,&nbsp;</ng-container>
                        <ng-container i18n="@@hermes-global-translations.zoom-image"> zoom image </ng-container>
                    </span>
                    }
                </div>
                }
            </div>
        </div>

        } }
    </swiper>

    <!-- Carousel navigation -->
    @if (isControlsDisplayed || (isMobile && isFocused) ){ @if(displayLeftControl || isLoopEnabled || isRewindEnabled ||
    (isMobile && isFocused)){
    <div
        class="left-control"
        [attr.aria-controls]="'carousel-items-' + carouselTitle"
        [style]="{ left: controlsInset.left, top: controlsInset.top }">
        <h-icon-button
            [name]="'carousel-control-left'"
            [id]="idControl"
            [border]="false"
            (focusout)="onFocusOut($event)"
            [borderRadius]="controlsBorderRadius"
            [changeOpacityOnHover]="controlsOpacity"
            [lineHeight]="'0'"
            [iconSize]="iconSize + 'px'"
            [isKeyboardNavigationActive]="isKeyboardNavigationActive"
            (pressed)="slidePrev($event)">
            <h-svg-icon [name]="'arrow-left-bold'"></h-svg-icon>
            <span i18n="@@hermes.accessibility.previous-image" hSrOnlyNoSnippet text>Previous image</span>
        </h-icon-button> </div
    >} @if(displayRightControl || isLoopEnabled || isRewindEnabled || (isMobile && isFocused)){
    <div
        class="right-control"
        [attr.aria-controls]="'carousel-items-' + carouselTitle"
        [style]="{ right: controlsInset.right, top: controlsInset.top }">
        <h-icon-button
            [name]="'carousel-control-right'"
            [id]="idControl"
            [border]="false"
            [borderRadius]="controlsBorderRadius"
            [changeOpacityOnHover]="controlsOpacity"
            [lineHeight]="'0'"
            [iconSize]="iconSize + 'px'"
            [isKeyboardNavigationActive]="isKeyboardNavigationActive"
            (pressed)="slideNext($event)"
            (focusout)="onFocusOut($event)">
            <h-svg-icon [name]="'arrow-right-bold'"></h-svg-icon>
            <span i18n="@@hermes.accessibility.next-image" hSrOnlyNoSnippet text>Next image</span>
        </h-icon-button> </div
    >} }
</div>
